import styled from "styled-components";
import { Nav, NavItem, UncontrolledCollapse } from "reactstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router";
import stringUtils from "../../_services/string-utils";
import { sortNumValuesByField } from "../../_services/sorting-helper";
import { useUserDropdownUtils } from "./UserDropDownUtils";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { adminAreaActions } from "../../admin/services/store/adminarea.actions";
import { ReportBugModal } from "../../admin/components/organizationAdmin/ReportBugModal";
import { selectOrganization } from "../../user/services/store/user.actions";
import { OrganizationType } from "../../_statics/OrganizationType";

const NavSidebar = () => {
    const navigation = useSelector((state) => state.navigation);
    const adminArea = useSelector((state) => state.adminArea);
    const authentication = useSelector((state) => state.authentication);
    const location = useLocation();
    const dispatch = useDispatch();
    const [accountOpen, setAccountOpen] = useState(false);
    const { userDropdownItems } = useUserDropdownUtils();
    const { isSuperUser, isTaxConsultant, selectedOrganization } = useUserAuthentication();

    const taxAgencyOrg = useMemo(() => {
        const orgs = authentication?.user?.availableOrganizations;
        return orgs ? orgs.find((o) => o.taxAgency) : null;
    }, [authentication?.user?.availableOrganizations]);

    const showBackToKanzlei =
        isTaxConsultant && taxAgencyOrg && selectedOrganization?.organizationType !== OrganizationType.TAX_AGENCY;

    const currentNavigation = useMemo(() => {
        if (navigation.items) {
            return navigation.items.find((item) => stringUtils.containsIgnoreCase(location.pathname, item.url));
        }
        return null;
    }, [location, navigation.items]);

    const organizationUUID = authentication?.user?.selectedOrganization?.externalUUID;

    return (
        <div className="bg-dark d-flex flex-column h-100">
            <UncontrolledCollapse navbar horizontal defaultOpen={true} toggler="sidebar-toggler">
                {navigation.items && navigation.items.length > 0 ? (
                    <CustomNav vertical>
                        {showBackToKanzlei && (
                            <>
                                <CustomNavItem className="always-hover">
                                    <SidebarActionButton
                                        type="button"
                                        onClick={() => dispatch(selectOrganization(taxAgencyOrg.externalUUID, true))}
                                        data-cy="back-to-kanzlei-button"
                                    >
                                        <i className="fa fa-arrow-left" />
                                        Zurück zur Kanzlei
                                    </SidebarActionButton>
                                </CustomNavItem>
                                <NavDivider />
                            </>
                        )}
                        {navigation.items &&
                            navigation.items.map((item, index) => {
                                if (item.title) {
                                    return <NavHeaderItem key={index}>{item.name}</NavHeaderItem>;
                                } else {
                                    return (
                                        <CustomNavItem key={index} className={item.url === "/" ? "always-hover" : ""}>
                                            <Link to={item.url}>
                                                <i
                                                    className={item.icon}
                                                    style={{
                                                        color:
                                                            currentNavigation && currentNavigation.url === item.url
                                                                ? "#20a8d8 !important"
                                                                : null,
                                                    }}
                                                />
                                                {item.name}
                                            </Link>
                                        </CustomNavItem>
                                    );
                                }
                            })}

                        {/* Divider before Account */}
                        <NavDivider />

                        {/* Account Section inside the Navigation */}
                        <CustomNavItem onClick={() => setAccountOpen(!accountOpen)} style={{ cursor: "pointer" }}>
                            <i className="fa fa-cog" style={{ marginRight: "0.75rem" }} />
                            Konto
                        </CustomNavItem>
                        {accountOpen && (
                            <DropdownContainer>
                                {sortNumValuesByField(userDropdownItems, "index")
                                    .filter((item) => !item.hidden)
                                    .map((item) => (
                                        <CustomNavItem key={item.index}>
                                            <Link to={item.url}>
                                                <i className={item.icon} style={{ marginRight: "0.5rem" }} />
                                                {item.name}
                                            </Link>
                                        </CustomNavItem>
                                    ))}
                            </DropdownContainer>
                        )}

                        {/* Bug Report Button for Super Users */}
                        {isSuperUser && organizationUUID && (
                            <>
                                <NavDivider />
                                <BugReportNavItem>
                                    <BugReportButton
                                        type="button"
                                        onClick={() => dispatch(adminAreaActions.toggleBugReportModal())}
                                        aria-label="Bug melden"
                                    >
                                        <i className="fa fa-bug" style={{ marginRight: "0.75rem" }} />
                                        Bug melden
                                    </BugReportButton>
                                </BugReportNavItem>
                            </>
                        )}
                    </CustomNav>
                ) : null}
            </UncontrolledCollapse>

            {isSuperUser && organizationUUID && (
                <ReportBugModal
                    isModalOpen={!!adminArea?.bugReportModalOpen}
                    bugReportLoading={adminArea?.bugReportLoading}
                    bugReportResult={adminArea?.bugReportResult}
                    bugReportError={adminArea?.bugReportError}
                    organizationUUID={organizationUUID}
                    dispatch={dispatch}
                    milestones={adminArea?.milestones}
                    milestonesLoading={adminArea?.milestonesLoading}
                    projectLabels={adminArea?.projectLabels}
                    projectLabelsLoading={adminArea?.projectLabelsLoading}
                />
            )}
        </div>
    );
};

// <SidebarLayout></SidebarLayout>

/*
        <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav">
                <li className="nav-item active">
                    <a className="nav-link" href="#">
                        Home <span class="sr-only">(current)</span>
                    </a>
                </li>
            </ul>
        </div>
         */

export default NavSidebar;

const CustomNav = styled(Nav)`
    width: 200px;
    flex-direction: row !important;

    & > * {
        width: 200px;
    }
`;

const CustomNavItem = styled(NavItem)`
    display: flex;
    align-items: center;
    color: var(--light) !important;
    padding: 0.75rem 1rem;
    cursor: pointer;

    &:hover,
    &.always-hover {
        /* Keep "Übersicht" highlighted */
        background: #20a8d8;
    }

    & a {
        display: flex;
        align-items: center;
        width: 100%;
        color: var(--light) !important;
        text-decoration: none;
        font-weight: 300;
    }

    & i {
        margin-right: 0.75rem;
        font-size: 1rem;
        color: #73818f;
    }
`;

const BugReportNavItem = styled(CustomNavItem)`
    padding: 0;

    &:hover {
        background: #e74c3c;
    }
`;

const BugReportButton = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1rem;
    background: none;
    border: none;
    color: var(--light) !important;
    font-weight: 300;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
    text-align: left;

    & i {
        color: #e74c3c;
    }
`;

const NavHeaderItem = styled.div`
    display: grid;
    text-transform: uppercase;
    color: var(--light);
    font-size: 80%;
    font-weight: 700;

    padding: 0.75rem 1rem;
`;

const DropdownContainer = styled.div`
    background: #333;
    border-radius: 5px;
    padding: 0.5rem 0;

    & ${CustomNavItem} {
        padding-left: 2rem;
        background: none;

        &:hover {
            background: #20a8d8;
        }
    }
`;

const NavDivider = styled.div`
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
    margin: 10px 0;
`;

const SidebarActionButton = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
`;
